import { FC, useEffect, useMemo, useState } from 'react';
import { Job } from 'containers/jobs/jobs.model';
import { useNavigate } from 'react-router-dom';
import { CustomButton, If } from 'components';
import ProfitabilitySummary, { ProfitabilitySummaryData } from 'containers/jobs/components/ProfitabilitySummary';
import RelatedEstimateCard from 'containers/estimates/components/RelatedEstimateCard';
import { AddIconWoPadding, WarningIcon } from 'assets/icons';
import RelatedInvoiceCard from 'containers/invoices/components/RelatedInvoiceCard';
import routesPath from 'routes/RoutesPath';
import BillingTabJobLineItemSection from 'containers/jobs/job-details/components/BillingTabJobLineItemSection';
import { ButtonType } from 'components/custom-button/CustomButton';
import { CreateEditJobNavEnum, JobStatus } from 'containers/jobs/constant';
import ExpensesTable from 'containers/jobs/job-details/components/ExpensesTable';
import { usePaginatedJobExpensesQuery } from 'containers/jobs/job-details/hooks/usePaginatedJobExpensesQuery';
import ExternalCompanyReferralTable from 'containers/jobs/job-details/components/ExternalComapanyReferralTable';
import {
  useDeleteJobExternalCompanyMutation,
  useEditJobExternalCompanyReferralMutation,
  useLazyGetJobCommissionSplitQuery
} from 'containers/jobs/api';
import CommissionsTable from 'containers/jobs/job-details/components/CommissionsTable';
import Accordion from 'components/accordion/Accordion';
import PayAdjustmentBanner from './PayAdjustmentBanner';
import { PayAdjustmentsTable } from './PayAdjustmentsTable';
import { useGetJobAdjustmentsQuery, useUpdateAdjustmentStatusMutation, useUpdateJobAdjustmentMutation } from '../api';
import { GetSuggestedAdjustmentsForJobResponse } from '../payrun.model';
import { getAmountWithCurrency } from 'utils/amountUtils';
import ReviewAdjustmentSuggestionsModal from './ReviewAdjustmentSuggestionsModal';
import { AdjustmentSourceType, AdjustmentStatus } from '../constants';
import { AdjustmentFormData } from '../payrun.model';
import { selectJobLineItems } from 'containers/jobs/create-edit-job/createJobSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  selectCurrentTeam,
  selectCurrentTeamAccountingPermissions,
  selectCurrentTeamJobPermissions,
  selectIsOwner
} from 'containers/app/appSlice';
import { checkIfInvoiceIsPaid } from 'containers/invoices/utils';
import { PayRunsCleverTapEvents } from '../events';
import { logAnalyticEvent } from 'utils/analytics';
import { SubscriptionStatus } from 'containers/app/app.model';
import { resetEstimateState } from 'containers/estimates/estimatesSlice';
import { StatusIndicator } from 'containers/invoices/constants';

interface JobReviewBillingTabProps {
  job: Job;
  profitabilityData?: ProfitabilitySummaryData;
  onInvoiceClick: (invoiceId: string) => void;
  onEstimateClick: (estimateId: string) => void;
  isLoading?: boolean;
  suggestedAdjustments?: GetSuggestedAdjustmentsForJobResponse;
  isFetchingSuggestedAdjustments?: boolean;
  refetchSuggestedAdjustments?: () => any;
}

const JobReviewBillingTab: FC<JobReviewBillingTabProps> = ({
  job,
  profitabilityData,
  onInvoiceClick,
  onEstimateClick,
  isLoading,
  suggestedAdjustments,
  isFetchingSuggestedAdjustments,
  refetchSuggestedAdjustments
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isOwner = useAppSelector(selectIsOwner);

  const { data: adjustments, refetch: refetchAdjustments } = useGetJobAdjustmentsQuery({
    teamId: job?.teamId,
    jobId: job.id
  });

  const [editExternalCompanyReferral, { isLoading: isEditingReferral }] = useEditJobExternalCompanyReferralMutation();
  const [deleteExternalCompany, { isLoading: isDeletingExternalCompany }] = useDeleteJobExternalCompanyMutation();
  const [getJobCommissionSplit, { data: commissionData }] = useLazyGetJobCommissionSplitQuery();
  const [updateAdjustment, { isLoading: isUpdatingAdjustment }] = useUpdateJobAdjustmentMutation();
  const [updateAdjustmentStatus, { isLoading: isUpdatingAdjustmentStatus }] = useUpdateAdjustmentStatusMutation();

  const jobLineItemsState = useAppSelector(selectJobLineItems);
  const { modifyDoneJobs, viewJobSource, editJobSource } = useAppSelector(selectCurrentTeamJobPermissions);
  const currentTeamAccountingPermissions = useAppSelector(selectCurrentTeamAccountingPermissions);
  const {
    managePayAdjustments,
    viewJobProfitability,
    manageOthersExpenses,
    viewMyJobCommissionsAndPay,
    createExpenses,
    overrideJobRatesAndCommissions
  } = currentTeamAccountingPermissions ?? {};

  const {
    list: expenses,
    loadMore,
    hasMore,
    summary: expensesSummary,
    isFetching,
    totalListCount,
    resetRecallQuery: resetExpensesRecallQuery,
    hasPermissionsToView: hasPermissionsToViewExpenses
  } = usePaginatedJobExpensesQuery({
    jobId: job.id
  });

  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [currentSuggestionIndex, setCurrentSuggestionIndex] = useState(0);
  const [suggestionsUpdated, setSuggestionsUpdated] = useState(false);

  const isPaidInvoice =
    job.invoices?.length > 0 &&
    job.invoices.some(invoice => checkIfInvoiceIsPaid(invoice.statusIndicator as StatusIndicator));

  const commissionList = useMemo(() => {
    return commissionData?.data || [];
  }, [commissionData]);

  useEffect(() => {
    getJobCommissionSplit({ teamId: job.teamId, jobId: job.id });
  }, [job.id, job.teamId, getJobCommissionSplit]);

  useEffect(() => {
    loadMore();
  }, [job.id, job.teamId]);

  const handleAddInvoiceFromJob = () => {
    navigate(routesPath.INVOICES_CREATE + `?jobId=${job.id}`);
  };

  const handleAddEstimateFromJob = () => {
    if (
      currentTeam?.subscriptionStatus === SubscriptionStatus.SUBSCRIPTION_EXPIRED ||
      currentTeam?.subscriptionStatus === SubscriptionStatus.NOT_SUBSCRIBED
    ) {
      if (isOwner) navigate(routesPath.ESTIMATE_INTEGRATION);
      else navigate(routesPath.ESTIMATES);
      return;
    }
    dispatch(resetEstimateState());
    navigate(routesPath.ESTIMATES_CREATE + `?jobId=${job.id}`);
  };

  const handleExternalCompanyEditAndSave = async (amount: number) => {
    await editExternalCompanyReferral({
      teamId: job.teamId,
      jobId: job.id,
      amount,
      externalCompanyId: job.externalCompany?.id || '',
      isEdited: true
    });
  };

  const handleExternalCompanyReset = async () => {
    await editExternalCompanyReferral({
      teamId: job.teamId,
      jobId: job.id,
      amount: job.externalCompany?.referralAmount || 0,
      externalCompanyId: job.externalCompany?.id || '',
      isEdited: false
    });
  };

  const handleExternalCompanyDelete = async () => {
    await deleteExternalCompany({
      teamId: job.teamId,
      jobId: job.id
    });
  };

  const handleReviewClick = () => {
    setCurrentSuggestionIndex(0);
    setIsReviewModalOpen(true);
    logAnalyticEvent(PayRunsCleverTapEvents.webPayRunAdjustmentReviewClick, {
      payRunId: job.id,
      merchantId: job.merchantId,
      source: 'payrun-job-review-billing-tab'
    });
  };

  const handleDiscard = async (adjustmentId: string) => {
    await updateAdjustmentStatus({
      adjustmentId,
      status: AdjustmentStatus.REJECTED,
      teamId: job.teamId
    });
    handleNextSuggestion();
  };

  const handleDecideLater = async (adjustmentData: AdjustmentFormData, adjustmentId: string, isEdited: boolean) => {
    if (isEdited) {
      await updateAdjustment({
        adjustmentId,
        teamId: job.teamId,
        jobId: job.id,
        payload: {
          teamId: job.teamId,
          jobId: job.id,
          ...adjustmentData
        }
      });
    }
    await updateAdjustmentStatus({
      adjustmentId,
      status: AdjustmentStatus.REMIND_ME_LATER,
      teamId: job.teamId
    });
    handleNextSuggestion();
  };

  const handleAcceptSuggestedAdjustment = async (
    adjustmentData: AdjustmentFormData,
    adjustmentId: string,
    isEdited: boolean
  ) => {
    if (isEdited) {
      await updateAdjustment({
        adjustmentId,
        teamId: job.teamId,
        jobId: job.id,
        payload: {
          teamId: job.teamId,
          jobId: job.id,
          ...adjustmentData
        }
      });
    }
    await updateAdjustmentStatus({
      adjustmentId,
      status: AdjustmentStatus.ACCEPTED,
      teamId: job.teamId
    });
    handleNextSuggestion();
  };

  const refetch = () => {
    refetchAdjustments();
    if (refetchSuggestedAdjustments) {
      refetchSuggestedAdjustments();
    }
    setSuggestionsUpdated(false);
  };

  const handleNextSuggestion = async () => {
    if (!refetchSuggestedAdjustments) return;

    const resp = await refetchSuggestedAdjustments();
    const adjustmentsData = resp?.data?.adjustments;
    if (adjustmentsData?.length === 0 || !adjustmentsData) {
      setIsReviewModalOpen(false);
      setCurrentSuggestionIndex(0);
      refetch();
      return;
    }
    setSuggestionsUpdated(true);
    if (suggestedAdjustments && currentSuggestionIndex < suggestedAdjustments.adjustments.length - 1) {
      setCurrentSuggestionIndex(prev => prev + 1);
    } else {
      setIsReviewModalOpen(false);
      setCurrentSuggestionIndex(0);
      refetch();
    }
  };

  const totalCommissions = useMemo(() => {
    if (!commissionList?.length) return 0;
    return commissionList.reduce((sum, item) => sum + (parseFloat(item.commissionAmount) || 0), 0);
  }, [commissionList]);

  const showNoLinkedDocuments = useMemo(() => {
    return !job.latestInvoice && (!job?.estimates || job.estimates.length === 0) && !modifyDoneJobs;
  }, [job.latestInvoice, job.estimates]);

  return (
    <div className="flex grow flex-col gap-6 p-6 text-primaryText">
      <If condition={suggestedAdjustments?.totalCount > 0 && managePayAdjustments}>
        <PayAdjustmentBanner
          isLoading={isFetchingSuggestedAdjustments}
          suggestionsCount={suggestedAdjustments?.totalCount || 0}
          onReviewClick={handleReviewClick}
        />
      </If>
      <div className="flex flex-col gap-4">
        <div className="text-17px font-bold">Related documents</div>

        <div className="flex max-w-[722px] gap-6">
          {showNoLinkedDocuments && <div className="text-17px font-bold text-primaryText">-</div>}
          {job.latestInvoice ? (
            <div className="w-1/2">
              <RelatedInvoiceCard
                invoice={job.latestInvoice}
                onInvoiceClick={onInvoiceClick}
                fromBillingTab
                showPaymentDetails={true}
                paymentInfo={job.paymentInfo}
              />
            </div>
          ) : (
            modifyDoneJobs &&
            job.isEditable && (
              <div
                className="flex w-1/2 cursor-pointer items-center justify-center rounded-md border 
          border-borderGray p-3 pr-4 hover:bg-secondaryBtn"
                role="presentation"
                onClick={handleAddInvoiceFromJob}>
                <div className="flex items-center">
                  <AddIconWoPadding className="mr-2 shrink-0" />
                  <div className="text-sbase font-semibold text-primaryBtn">Add invoice</div>
                </div>
              </div>
            )
          )}

          <If condition={!!job.estimates?.[0]}>
            <div className="w-1/2">
              <RelatedEstimateCard
                estimate={job.estimates?.[0]}
                onEstimateClick={onEstimateClick}
                fromBillingTab
                showExpiryDate={true}
              />
            </div>
          </If>
          <If
            condition={
              !job?.latestInvoice && (!job?.estimates || job.estimates.length === 0) && modifyDoneJobs && job.isEditable
            }>
            <div
              className="flex w-1/2 cursor-pointer items-center justify-center rounded-md border 
                    border-borderGray p-3 pr-4 hover:bg-secondaryBtn"
              role="presentation"
              onClick={handleAddEstimateFromJob}>
              <div className="flex items-center">
                <AddIconWoPadding className="mr-2 shrink-0" />
                <div className="text-sbase font-semibold text-primaryBtn">Add estimate</div>
              </div>
            </div>
          </If>
        </div>
      </div>
      <hr className="w-full border-secondaryBtn" />
      <If condition={jobLineItemsState?.length > 0 && viewJobProfitability}>
        <div>
          <div className="mb-3 text-17px font-bold text-primaryText">Profitability</div>
          <ProfitabilitySummary data={profitabilityData} isPaidInvoice={isPaidInvoice} isLoading={isLoading} />
        </div>
        <hr className="w-full border-secondaryBtn" />
      </If>
      <div className="relative flex flex-col gap-2">
        {job.isEditable && modifyDoneJobs && !isPaidInvoice && (
          <div className="absolute right-0">
            <CustomButton
              id="edit-line-items-button"
              type={ButtonType.SECONDARY}
              className="h-auto border-none p-0 text-secondary shadow-none hover:bg-white"
              onClick={() =>
                navigate(routesPath.EDIT_JOB.replace(':jobId', job.id), {
                  state: {
                    selectedJobTab: CreateEditJobNavEnum.LINE_ITEMS
                  }
                })
              }>
              Edit line items
            </CustomButton>
          </div>
        )}
        <div>
          <BillingTabJobLineItemSection job={job} />
        </div>
      </div>
      <hr className="w-full border-secondaryBtn" />
      <If condition={manageOthersExpenses || createExpenses}>
        <Accordion
          title={
            <>
              {manageOthersExpenses ? 'Expenses' : 'My expenses'}{' '}
              {totalListCount > 0 && <span>({totalListCount})</span>}
            </>
          }
          headerRight={
            <span className="whitespace-nowrap text-17px font-semibold text-headingGray">
              Total expense {getAmountWithCurrency(expensesSummary?.total || 0)}
            </span>
          }
          initialOpen={false}>
          <ExpensesTable
            jobId={job.id}
            expenses={expenses}
            loadMore={loadMore}
            hasMore={hasMore}
            isFetching={isFetching}
            summary={expensesSummary}
            resetRecallQuery={resetExpensesRecallQuery}
            jobStatus={job?.status}
            hasPermissionsToView={hasPermissionsToViewExpenses}
            isJobEditable={job?.isEditable}
            jobMembers={job.assignees}
          />
        </Accordion>
        <hr className="w-full border-secondaryBtn" />
      </If>

      {viewJobSource && (
        <>
          <Accordion
            title={
              <>
                External company referral
                <If condition={job.status !== JobStatus.DONE}>
                  <span> (estimated)</span>
                </If>
              </>
            }
            headerRight={
              <span className="whitespace-nowrap text-17px font-semibold text-headingGray">
                Total referral ${job?.externalCompany?.referralAmount?.toFixed(2) || '0.00'}
              </span>
            }
            initialOpen={false}>
            <ExternalCompanyReferralTable
              jobNumber={job.jobNumber}
              referralData={job?.externalCompany}
              onEditAndSave={handleExternalCompanyEditAndSave}
              onDelete={handleExternalCompanyDelete}
              isLoading={isDeletingExternalCompany || isEditingReferral}
              onReset={handleExternalCompanyReset}
              jobStatus={job.status}
              isJobEditable={job.isReferralEditable && editJobSource}
            />
          </Accordion>
          <hr className="w-full border-secondaryBtn" />
        </>
      )}
      <If condition={overrideJobRatesAndCommissions || viewMyJobCommissionsAndPay}>
        <Accordion
          title={
            <>
              {overrideJobRatesAndCommissions ? 'Commissions' : 'My commissions'}
              <If condition={job.status !== JobStatus.DONE}>
                <span> (estimated)</span>
              </If>
            </>
          }
          headerRight={
            <span className="whitespace-nowrap text-17px font-semibold text-headingGray">
              Total commissions ${totalCommissions.toFixed(2)}
            </span>
          }
          initialOpen={false}>
          <CommissionsTable job={job} commissionData={commissionList} />
        </Accordion>
        <hr className="w-full border-secondaryBtn" />
      </If>
      <If condition={managePayAdjustments || viewMyJobCommissionsAndPay}>
        <Accordion
          title={
            <div className="flex items-center gap-1">
              {managePayAdjustments ? 'Pay adjustments' : 'My pay adjustments'}
              <If condition={suggestedAdjustments?.totalCount > 0 && managePayAdjustments}>
                <span className="flex items-center gap-1">
                  <WarningIcon className="path-stroke-current h-4 w-4 text-pending-orange" />
                </span>
              </If>
            </div>
          }
          headerRight={
            <span className="whitespace-nowrap text-17px font-semibold text-headingGray">
              Total adjustments {getAmountWithCurrency(adjustments?.totalAmount)}
            </span>
          }
          initialOpen={false}>
          <div className="p-4">
            <div className="my-3 flex flex-col justify-center gap-2">
              <If condition={suggestedAdjustments?.totalCount > 0 && managePayAdjustments}>
                <PayAdjustmentBanner
                  isLoading={isFetchingSuggestedAdjustments}
                  suggestionsCount={suggestedAdjustments?.totalCount || 0}
                  onReviewClick={handleReviewClick}
                />
              </If>
              <PayAdjustmentsTable
                adjustments={adjustments?.adjustments || []}
                job={job}
                refetchAdjustments={refetchAdjustments}
              />
            </div>
          </div>
        </Accordion>
      </If>
      <If condition={isReviewModalOpen && suggestedAdjustments && suggestedAdjustments.adjustments?.length > 0}>
        <ReviewAdjustmentSuggestionsModal
          open={isReviewModalOpen}
          onClose={() => {
            setIsReviewModalOpen(false);
            setCurrentSuggestionIndex(0);
            if (suggestionsUpdated) {
              refetch();
            }
          }}
          suggestedAdjustment={suggestedAdjustments?.adjustments?.[currentSuggestionIndex]}
          onDiscard={handleDiscard}
          onDecideLater={handleDecideLater}
          onAddAdjustment={handleAcceptSuggestedAdjustment}
          currentIndex={currentSuggestionIndex}
          totalSuggestions={suggestedAdjustments?.totalCount || 0}
          assignees={job.assignees}
          sourceType={AdjustmentSourceType.JOB}
          isLoading={isUpdatingAdjustment || isUpdatingAdjustmentStatus || isFetchingSuggestedAdjustments}
        />
      </If>
    </div>
  );
};

export default JobReviewBillingTab;
